import { IS_SERVER } from '../common/constants';
import { MARKET, META_PIXEL_ID, REDDIT_PIXEL_ID } from '../config/browser';

export const UK_PIXEL_EVENTS = {
  CINEMA_SHOWTIME_CLICK: 'CinemaShowtimeClick',
  FILM_PURCHASE_CLICK: 'FilmPurchaseClick',
  MOVIE_PAGE_VIEW: 'MoviePageView',
  TRAILER_START: 'TrailerStart',
  NEWSLETTER_SIGNUP: 'NewsletterSignup',
  HBO_MAX_CLICK: 'HboMaxClick',
} as const;

export type UKPixelEvent = typeof UK_PIXEL_EVENTS[keyof typeof UK_PIXEL_EVENTS];

export interface UKPixelEventParams {
  [key: string]: string | number | boolean | undefined;
}

declare const window: any;

const trackingConsentGroup = 'C0004';

const isUkMarket = (): boolean => MARKET === 'UK';

const hasTrackingConsent = (): boolean => {
  if (IS_SERVER || typeof window === 'undefined' || !isUkMarket()) {
    return false;
  }

  if (!window.Optanon) {
    return true;
  }

  const activeGroups = window.OptanonActiveGroups;
  return typeof activeGroups === 'string' && activeGroups.split(',').indexOf(trackingConsentGroup) !== -1;
};

const insertScript = (id: string, src: string): void => {
  if (document.getElementById(id)) {
    return;
  }

  const script = document.createElement('script');
  script.id = id;
  script.async = true;
  script.src = src;
  document.head.appendChild(script);
};

const initializeMetaPixel = (): void => {
  if (window.fbq) {
    return;
  }

  const fbq: any = (...args: any[]) => {
    fbq.callMethod ? fbq.callMethod(...args) : fbq.queue.push(args);
  };
  fbq.push = fbq;
  fbq.loaded = true;
  fbq.version = '2.0';
  fbq.queue = [];
  window.fbq = fbq;
  window._fbq = fbq;

  insertScript('uk-meta-pixel', 'https://connect.facebook.net/en_US/fbevents.js');
  fbq('init', META_PIXEL_ID);
};

const initializeRedditPixel = (): void => {
  if (window.rdt) {
    return;
  }

  const rdt: any = (...args: any[]) => {
    rdt.sendEvent ? rdt.sendEvent(...args) : rdt.callQueue.push(args);
  };
  rdt.callQueue = [];
  window.rdt = rdt;

  insertScript('uk-reddit-pixel', `https://www.redditstatic.com/ads/pixel.js?pixel_id=${REDDIT_PIXEL_ID}`);
  rdt('init', REDDIT_PIXEL_ID);
};

export const initializeUKMarketingPixels = (): boolean => {
  if (!hasTrackingConsent()) {
    return false;
  }

  initializeMetaPixel();
  initializeRedditPixel();
  return true;
};

export const trackUKPageView = (): void => {
  if (!initializeUKMarketingPixels()) {
    return;
  }

  window.fbq('track', 'PageView');
  window.rdt('track', 'PageVisit');
};

export const trackUKPixelEvent = (event: UKPixelEvent, params: UKPixelEventParams = {}): void => {
  if (!initializeUKMarketingPixels()) {
    return;
  }

  window.fbq('trackCustom', event, params);
  window.rdt('track', 'Custom', { ...params, customEventName: event });
};

export const addUKPixelConsentListener = (): (() => void) => {
  if (IS_SERVER || typeof window === 'undefined' || !isUkMarket()) {
    return () => undefined;
  }

  const onConsentChange = () => {
    if (initializeUKMarketingPixels()) {
      window.fbq('track', 'PageView');
      window.rdt('track', 'PageVisit');
    }
  };
  window.addEventListener('consent.onetrust', onConsentChange);
  initializeUKMarketingPixels();

  return () => window.removeEventListener('consent.onetrust', onConsentChange);
};
